import React, { useCallback, useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  TableCellsIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from 'components/icons';

import { aiApi } from '../../services/api';

// =============================================================================
// Bid schedule
// =============================================================================
//
// The solicitation asks for "a completed Attachment B-Bid Schedule pricing
// sheet" — the agency's own workbook, filled in. A price narrative that reaches
// the same total does not satisfy that, and before this the user had to open
// Excel and type thirty-five numbers by hand.
//
// So this prices the real line items and hands back the real file. The workbook
// that downloads is the agency's original with values written into it, not a
// reconstruction: a schedule carries formatting, notes and merged headings that
// a rebuild would lose, and a contracting officer notices.

interface LineItem {
  itemNo: string;
  description: string;
  quantity: number | null;
  unit: string | null;
  group: string | null;
  isHeader: boolean;
  row: number;
  unitPrice: number | null;
}

interface Finding {
  code: string;
  severity: 'blocking' | 'warning';
  title: string;
  detail: string;
}

interface Schedule {
  reconciliation: { findings: Finding[]; clean: boolean; scheduleTotal: number } | null;
  filename: string;
  fillable: boolean;
  warnings: string[];
  lineItems: LineItem[];
  periods: string[];
}

const money = (n: number) =>
  n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const BidSchedulePanel: React.FC<{ proposalId: string }> = ({ proposalId }) => {
  const [schedule, setSchedule] = useState<Schedule | null>(null);
  const [prices, setPrices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await aiApi.getBidSchedule(proposalId);
      const data: Schedule = res.data;
      setSchedule(data);
      const seeded: Record<string, string> = {};
      data.lineItems.forEach((i) => {
        if (i.unitPrice != null) seeded[i.itemNo] = String(i.unitPrice);
      });
      setPrices(seeded);
    } catch (err: any) {
      // A solicitation with no pricing spreadsheet is the normal case, not a
      // failure — say which one this is rather than showing an error.
      setError(err?.response?.data?.error || 'Could not load the bid schedule.');
    } finally {
      setLoading(false);
    }
  }, [proposalId]);

  useEffect(() => {
    load();
  }, [load]);

  const priceable = useMemo(
    () => schedule?.lineItems.filter((i) => !i.isHeader) ?? [],
    [schedule],
  );
  const filledCount = priceable.filter((i) => (prices[i.itemNo] ?? '').trim() !== '').length;

  const extendedFor = (item: LineItem): number | null => {
    const raw = prices[item.itemNo];
    const n = Number(raw);
    if (!raw || !Number.isFinite(n) || item.quantity == null) return null;
    return n * item.quantity;
  };

  const periodTotal = (period: string | null) =>
    priceable
      .filter((i) => i.group === period)
      .reduce((sum, i) => sum + (extendedFor(i) ?? 0), 0);

  const save = async () => {
    setSaving(true);
    try {
      await aiApi.saveBidSchedule(proposalId, prices);
      // Reloaded so the reconciliation reflects what was just typed rather than
      // what was on screen when the panel opened.
      await load();
      toast.success('Prices saved');
    } catch {
      toast.error('Could not save prices');
    } finally {
      setSaving(false);
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      // Saved first, deliberately. Downloading a sheet that does not match what
      // is on screen is the kind of thing nobody notices until after submission.
      await aiApi.saveBidSchedule(proposalId, prices);
      const res = await aiApi.downloadBidSchedule(proposalId);
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = schedule?.filename || 'bid-schedule.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Completed schedule downloaded');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not build the completed schedule');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return <p className="text-sm text-content-muted">Looking for a bid schedule…</p>;

  if (error || !schedule) {
    return (
      <div className="rounded-lg border border-dashed border-border-strong p-6 text-center">
        <TableCellsIcon className="mx-auto h-7 w-7 text-content-subtle" />
        <p className="mt-2 text-sm text-content-muted">{error}</p>
        <p className="mt-1 text-xs text-content-subtle">
          If the solicitation does attach one, it may be a format Clevor could not read — check the
          documents before pricing by hand.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-title text-content">{schedule.filename}</h3>
          <p className="mt-0.5 text-sm text-content-muted">
            {filledCount} of {priceable.length} lines priced. The completed sheet is a submission
            item in its own right — the price narrative does not replace it.
          </p>
        </div>
        <div className="flex flex-shrink-0 gap-2">
          <button onClick={save} disabled={saving} className="btn-secondary px-4 py-2 text-sm disabled:opacity-60">
            {saving ? 'Saving…' : 'Save'}
          </button>
          <button
            onClick={download}
            disabled={downloading || filledCount === 0}
            title={filledCount === 0 ? 'Price at least one line first' : undefined}
            className="btn-primary flex items-center gap-1.5 px-4 py-2 text-sm disabled:opacity-50"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            {downloading ? 'Building…' : 'Download completed sheet'}
          </button>
        </div>
      </div>

      {/* The schedule and the price narrative are two documents saying the same
          thing, and the government reads both. Each is correct on its own
          screen; only the comparison shows a twenty-thousand-dollar
          disagreement, so the comparison is made here rather than left to
          somebody noticing. */}
      {schedule.reconciliation?.findings?.map((f, i) => (
        <div
          key={i}
          className={`flex gap-2.5 rounded-lg p-3 ${
            f.severity === 'blocking'
              ? 'border-2 border-red-400 bg-red-50'
              : 'border border-amber-300 bg-amber-50'
          }`}
        >
          <ExclamationTriangleIcon
            className={`h-5 w-5 flex-shrink-0 ${f.severity === 'blocking' ? 'text-red-600' : 'text-amber-600'}`}
          />
          <div className="min-w-0">
            <p className={`font-semibold ${f.severity === 'blocking' ? 'text-red-900' : 'text-amber-900'}`}>
              {f.title}
            </p>
            <p className={`mt-0.5 text-sm ${f.severity === 'blocking' ? 'text-red-800' : 'text-amber-800'}`}>
              {f.detail}
            </p>
          </div>
        </div>
      ))}

      {schedule.warnings.map((w, i) => (
        <div key={i} className="flex gap-2.5 rounded-lg border border-amber-300 bg-amber-50 p-3">
          <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <p className="text-sm text-amber-900">{w}</p>
        </div>
      ))}

      {schedule.periods.map((period) => {
        const rows = schedule.lineItems.filter((i) => i.group === period);
        if (rows.length === 0) return null;
        return (
          <div key={period} className="overflow-hidden rounded-lg border border-border">
            <div className="flex items-baseline justify-between gap-3 border-b border-border bg-surface-sunk px-4 py-2">
              <span className="font-semibold text-content">{period}</span>
              <span className="text-sm text-content-muted">
                Period total{' '}
                <span className="font-mono font-medium text-content">
                  ${money(periodTotal(period))}
                </span>
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-content-subtle">
                    <th className="px-4 py-2 font-medium">Item</th>
                    <th className="px-2 py-2 font-medium">Description</th>
                    <th className="px-2 py-2 text-right font-medium">Qty</th>
                    <th className="px-2 py-2 font-medium">Unit</th>
                    <th className="px-2 py-2 text-right font-medium">Unit price</th>
                    <th className="px-4 py-2 text-right font-medium">Extended</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => {
                    const extended = extendedFor(item);
                    return (
                      <tr
                        key={`${item.itemNo}-${item.row}`}
                        className={`border-b border-border last:border-0 ${item.isHeader ? 'bg-surface-sunk/50' : ''}`}
                      >
                        <td className="whitespace-nowrap px-4 py-2 font-mono text-xs text-content">
                          {item.itemNo}
                        </td>
                        <td className="px-2 py-2 text-content-muted">{item.description}</td>
                        <td className="px-2 py-2 text-right font-mono text-xs text-content-muted">
                          {item.quantity ?? ''}
                        </td>
                        <td className="px-2 py-2 text-xs text-content-muted">{item.unit ?? ''}</td>
                        <td className="px-2 py-2 text-right">
                          {item.isHeader ? (
                            // A parent line is priced by its children; an input
                            // here would double-count it in the total.
                            <span className="text-xs text-content-subtle">priced below</span>
                          ) : (
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              inputMode="decimal"
                              value={prices[item.itemNo] ?? ''}
                              onChange={(e) =>
                                setPrices((prev) => ({ ...prev, [item.itemNo]: e.target.value }))
                              }
                              placeholder="—"
                              aria-label={`Unit price for ${item.itemNo}`}
                              className="w-28 rounded-md border border-border px-2 py-1 text-right font-mono text-sm focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                            />
                          )}
                        </td>
                        <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-xs text-content">
                          {extended != null ? `$${money(extended)}` : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {filledCount === priceable.length && priceable.length > 0 && (
        <div className="flex gap-2.5 rounded-lg border border-green-300 bg-green-50 p-3">
          <CheckCircleIcon className="h-5 w-5 flex-shrink-0 text-green-600" />
          <p className="text-sm text-green-900">
            Every line is priced. Download the completed sheet and submit it with your quote — it
            keeps the agency's original filename and formatting, with extended amounts written as
            live formulas.
          </p>
        </div>
      )}
    </div>
  );
};

export default BidSchedulePanel;
